import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import { apiService } from '../../../services/api';
import './Registration.scss';

interface DonationRegistrationProps {
  event: any;
  category: any;
  onSuccess?: (data: any) => void;
  onCancel?: () => void;
}

const DonationRegistration: React.FC<DonationRegistrationProps> = ({ 
  event, 
  category, 
  onSuccess, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    donation: category.show_any_amount ? '' : category.price,
    message: '',
    anonymous: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value 
    }));
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData(prev => ({ ...prev, phone: value || '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.registration.donation({
        event_slug: event.slug,
        category_id: category.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        donation: parseFloat(formData.donation.toString()),
        message: formData.message,
        anonymous: formData.anonymous
      });

      if (response.success) {
        setSuccess(true);
        
        // Dispatch cart update event to notify header
        window.dispatchEvent(new CustomEvent('cartUpdated', {
          detail: { message: 'Cart updated successfully' }
        }));
        
        onSuccess?.(response);
      } else {
        setError(response.error || 'Donation failed');
      }
    } catch (err: any) {
      setError(err.message || 'Donation failed');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (amount: number) => {
    const symbol = event.setting.currency?.symbol || '$';
    return `${symbol}${amount.toLocaleString()}`;
  };

  if (success) {
    return (
      <div className="registration-success">
        <Card className="text-center">
          <Card.Body>
            <div className="success-icon mb-3">
              <i className="fas fa-heart text-success" style={{ fontSize: '3rem' }}></i>
            </div>
            <h3 className="text-success mb-3">Donation Successful!</h3>
            <p className="text-muted mb-4">
              Thank you for your generous donation of <strong>{formatPrice(parseFloat(formData.donation.toString()))}</strong>.
            </p>
            <p className="text-muted mb-4">
              A confirmation email has been sent to <strong>{formData.email}</strong>.
            </p>
            <Button variant="primary" onClick={onCancel}>
              Close
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="donation-registration">
      <Card>
        <Card.Header>
          <h4 className="mb-0">Make a Donation</h4>
          <p className="text-muted mb-0">Support {event.title}</p>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="required">Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your name"
                className="simple-input"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="required">Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email address"
                    className="simple-input"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <InternationalPhoneInput
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="Enter your phone number"
                    id="phone"
                    name="phone"
                    label="Phone Number"
                    showLabel={true}
                    required={true}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="required">Donation Amount</Form.Label>
              <div className="input-group">
                <span className="input-group-text">
                  {event.setting.currency?.symbol || '$'}
                </span>
                <Form.Control
                  type="number"
                  name="donation"
                  value={formData.donation}
                  onChange={handleInputChange}
                  required
                  min="1"
                  step="0.01"
                  placeholder={category.show_any_amount ? "Enter amount" : category.price.toString()}
                  disabled={!category.show_any_amount}
                  className="simple-input"
                />
              </div>
              {!category.show_any_amount && (
                <Form.Text className="text-muted">
                  Fixed donation amount: {formatPrice(category.price)}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Leave a message with your donation"
                className="simple-input"
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Check
                type="checkbox"
                name="anonymous"
                checked={formData.anonymous}
                onChange={handleInputChange}
                label="Make this donation anonymous"
              />
            </Form.Group>

            <div className="registration-actions">
              <Button
                type="button"
                variant="link"
                onClick={onCancel}
                className="btn-cancel"
              >
                <i className="fas fa-times"></i>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  `Donate ${formatPrice(parseFloat(formData.donation.toString()))}`
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DonationRegistration;
